import type { Customer } from '@/types/checkout';
import { isCountryCodeUSA } from './tax';

function validateUSZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{5}(-\d{4})?$/.test(value);
}

/**
 * Formats a Canadian postal code by adding a space after the first 3 characters
 * @param value - The postal code value (with or without space)
 * @returns Formatted postal code with space (e.g., "A1A 1A1")
 */
export function formatCanadianPostalCode(value: string): string {
  if (!value) return value;

  // Remove any existing spaces and convert to uppercase
  const cleaned = value.replace(/\s+/g, '').toUpperCase();

  // If we have 3 or more characters, add space after 3rd character
  if (cleaned.length >= 3) {
    return cleaned.slice(0, 3) + ' ' + cleaned.slice(3);
  }

  return cleaned;
}

function validateCAZip(value: string): boolean {
  if (!value) {
    return false;
  }

  const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
  return regex.test(value);
}

function validateNZZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{4}$/.test(value);
}

function validateUKZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(value);
}

function validateAUZip(value: string): boolean {
  if (!value) {
    return false;
  }
  return /^\d{4}$/.test(value);
}

export function getZipValidator(countryCode: string): (value: string) => boolean {
  switch (countryCode.toUpperCase()) {
    case 'CA':
      return validateCAZip;
    case 'GB':
    case 'UK':
      return validateUKZip;
    case 'NZ':
      return validateNZZip;
    case 'AU':
      return validateAUZip;
    default:
      return validateUSZip;
  }
}

export function validateZipCode(value: string, countryCode: string = 'US'): boolean {
  const validator = getZipValidator(countryCode);
  return validator(value);
}

export function getZipCodeErrorMessageKey(customer: Customer): string {
  const isUSA = isCountryCodeUSA(customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '');
  return isUSA ? 'fieldErrors.invalidZipCode' : 'fieldErrors.invalidPostalCode';
}
